import { useState, useRef, useEffect, useMemo } from "react";
import { Search } from "react-feather";
import ReactTooltip from "react-tooltip";

const FilterSectionPanel = ({ section, draftValue, onDraftChange, onApply }) => {
  const { options, placeholder, displayName, valueSearch = true, maxSelections = 5 } = section;
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOptions = useMemo(() => {
    const searchTermLower = searchTerm.toLowerCase().trim();
    const filtered = options.filter((option) => {
      const isLabelMatch = typeof option.label === "string" ? option.label?.toLowerCase().includes(searchTermLower) : option.labelValue?.toLowerCase().includes(searchTermLower);
      const isValueMatch = option.value.toLowerCase().includes(searchTermLower);
      return valueSearch ? isLabelMatch || isValueMatch : isLabelMatch;
    });

    if (searchTermLower.length === 2) {
      return filtered.sort((a) => (a.value.toLowerCase() === searchTermLower ? -1 : 1));
    }

    return filtered;
  }, [options, searchTerm, valueSearch]);

  const isOptionSelected = (option) => draftValue.some((item) => item.value === option.value);
  const isOptionDisabled = (option) => draftValue.length >= maxSelections && !isOptionSelected(option);

  const handleSelect = (option, isChecked) => {
    if (isChecked) {
      if (draftValue.length >= maxSelections) return;
      onDraftChange([...draftValue, option]);
    } else {
      onDraftChange(draftValue.filter((item) => item.value !== option.value));
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-w-[240px]">
      <span className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-px-4 tw-pt-4">{displayName}</span>
      <div className="tw-px-4 tw-mt-3 tw-mb-2 tw-relative">
        <Search size={14} color="#101828" className="tw-absolute tw-top-2.5 tw-left-6" />
        <input type="text" className="tw-w-full tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-pl-6 tw-bg-white tw-text-sm" placeholder={placeholder} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
      </div>
      {draftValue.length >= maxSelections && <div className="tw-px-4 tw-py-1 tw-text-xs tw-text-yellow-700">Maximum {maxSelections} selections reached</div>}
      <div className="tw-max-h-40 tw-overflow-y-auto tw-flex tw-flex-col">
        {filteredOptions.length === 0 ? (
          <div className="tw-px-4 tw-py-2 tw-text-gray-500 tw-text-sm tw-text-center">No options found</div>
        ) : (
          filteredOptions.map((option) => {
            const isSelected = isOptionSelected(option);
            const isDisabled = isOptionDisabled(option);
            return (
              <div key={option.value} className={`tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-px-4 tw-py-1 hover:tw-bg-gray-50 ${isDisabled ? "tw-opacity-50 tw-cursor-not-allowed" : "tw-cursor-pointer"}`} onClick={() => !isDisabled && handleSelect(option, !isSelected)}>
                <label className="form-check !tw-mb-0 tw-cursor-pointer">
                  <input className="form-check-input position-relative mr-0" type="checkbox" checked={isSelected} disabled={isDisabled} onChange={(e) => handleSelect(option, e.target.checked)} onClick={(e) => e.stopPropagation()} />
                  <span className="form-check-label"></span>
                </label>
                <span className={isDisabled ? "tw-text-gray-400" : ""}>{option.label}</span>
              </div>
            );
          })
        )}
      </div>
      <div className="tw-p-4">
        <button type="button" className="tw-w-full tw-rounded-lg tw-bg-primary-purple-500 tw-text-white tw-text-sm tw-font-medium tw-py-2 hover:tw-bg-primary-purple-600 tw-border-0" onClick={onApply}>
          Apply
        </button>
      </div>
    </div>
  );
};

const ContactFiltersDropdown = ({ sections, onApply, onClearAll }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [subPanelTop, setSubPanelTop] = useState(0);
  const [drafts, setDrafts] = useState({});
  const dropdownRef = useRef(null);
  const mainPanelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setActiveKey(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalSelected = sections.reduce((sum, section) => sum + section.value.length, 0);
  const activeSection = sections.find((section) => section.key === activeKey) ?? null;
  const activeDraftValue = activeSection ? (drafts[activeSection.key] ?? activeSection.value) : [];

  const handleSelectRow = (key, btn) => {
    const panelTop = mainPanelRef.current?.getBoundingClientRect().top ?? 0;
    const btnTop = btn.getBoundingClientRect().top;
    setSubPanelTop(btnTop - panelTop);
    setActiveKey(key);
  };

  const handleApplySection = () => {
    if (!activeSection) return;
    const draftValue = activeDraftValue;
    activeSection.onChange(draftValue);
    onApply({ [activeSection.overrideKey ?? activeSection.key]: draftValue });
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[activeSection.key];
      return next;
    });
    setActiveKey(null);
  };

  const handleClearAll = () => {
    setDrafts({});
    setActiveKey(null);
    onClearAll();
  };

  const handleClearSection = (section) => {
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[section.key];
      return next;
    });
    section.onClear();
    if (activeKey === section.key) setActiveKey(null);
  };

  const selectedLabelsTooltip = (section) => section.value.map((item) => item.label).join(", ");

  return (
    <div className="tw-relative" ref={dropdownRef}>
      <button type="button" onClick={() => setIsOpen((prev) => !prev)} className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-autoSuggestPill">
        <span className="material-symbols-outlined tw-text-[18px] tw-text-gray-900 tw-leading-none">tune</span>
        <span>Filters</span>
        {totalSelected > 0 && (
          <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-primary-purple-600 tw-bg-primary-purple-100 tw-py-0.5 tw-px-1.5 tw-rounded-full" data-for="contact-filters-total-tooltip" data-tip={sections.map(selectedLabelsTooltip).filter(Boolean).join(" | ")}>
            <span>{totalSelected}</span>
            <span
              className="material-symbols-outlined tw-text-[14px] tw-cursor-pointer tw-leading-none"
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}>
              close
            </span>
          </span>
        )}
        <ReactTooltip className="trademo-tooltip" backgroundColor="#8C8C8C" textColor="#FFFFFF" place="bottom" id="contact-filters-total-tooltip" />
      </button>

      {isOpen && (
        <div className="tw-absolute tw-z-50 tw-mt-1 tw-right-0">
          {activeSection && (
            <div style={{ top: subPanelTop }} className="tw-absolute tw-left-[calc(100%+8px)] tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg tw-overflow-hidden">
              <FilterSectionPanel key={activeSection.key} section={activeSection} draftValue={activeDraftValue} onDraftChange={(nextDraft) => setDrafts((prev) => ({ ...prev, [activeSection.key]: nextDraft }))} onApply={handleApplySection} />
            </div>
          )}

          <div ref={mainPanelRef} className="tw-w-[240px] tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-flex tw-flex-col tw-py-1">
            {sections.map((section) => {
              const selected = activeKey === section.key;
              return (
                <button key={section.key} type="button" className={`tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2.5 tw-text-sm tw-border-0 tw-bg-transparent tw-w-full tw-text-left ${selected ? "tw-bg-primary-purple-50 tw-text-primary-purple-700" : "tw-text-gray-700 hover:tw-bg-gray-50"}`} onClick={(e) => handleSelectRow(section.key, e.currentTarget)}>
                  <span className="tw-flex-1 tw-truncate">{section.displayName}</span>
                  {section.value.length > 0 && (
                    <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-primary-purple-600 tw-bg-primary-purple-100 tw-py-0.5 tw-px-1.5 tw-rounded-full tw-flex-shrink-0" data-for={`contact-filters-tooltip-${section.key}`} data-tip={selectedLabelsTooltip(section)}>
                      <span>{section.value.length}</span>
                      <span
                        className="material-symbols-outlined tw-text-[14px] tw-cursor-pointer tw-leading-none"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearSection(section);
                        }}>
                        close
                      </span>
                    </span>
                  )}
                  <span className={`material-symbols-outlined tw-text-[18px] tw-leading-none ${selected ? "tw-text-primary-purple-400" : "tw-text-gray-400"}`}>chevron_right</span>
                </button>
              );
            })}
          </div>
          {sections.map((section) => (section.value.length > 0 ? <ReactTooltip key={section.key} className="trademo-tooltip" backgroundColor="#8C8C8C" textColor="#FFFFFF" place="left" id={`contact-filters-tooltip-${section.key}`} /> : null))}
        </div>
      )}
    </div>
  );
};

export default ContactFiltersDropdown;
